import {currentVersions, EnvironmentResult, minimumVersions} from "../Support";
import React, {ReactNode, useCallback} from "react";
import IconButton from "../../ui/IconButton/IconButton";
import Icons from "../../ui/IconButton/Icons";
import {Link} from "wouter";
import util from "../../lib/util";
import ErrorBoundaryO from "../../ErrorBoundary/ErrorBoundaryO";

function Environment(props: EnvironmentProps) {

    /**
     * @param {number} status = 0: error, 1: success, 2: warning
     */
    const renderCheck = useCallback((status: number) => {
        if (status === 2) return (<IconButton color='orange' type={Icons.ERROR}/>);
        else if (status) return (<IconButton color='green' type={Icons.CHECKCIRCLE}/>);
        else return (<IconButton color='red' type={Icons.ERROR}/>);
    }, []);

    /**
     * @param {string} label
     * @param {number} status
     * @param {number | string} expected
     * @param {number | string} received
     */
    const renderTableCell = useCallback((label: string, status: number, expected: number | string, received: number | string, remedy: ReactNode) => {
        return (
            <tr className={status === 0 ? 'table-danger' : 'table-success'} key={label}>
                <td>{label}</td>
                <td className='table-centered'>{renderCheck(status)}</td>
                <td>{expected}</td>
                <td>{received}</td>
                <td>{(!status) ? (remedy) : 'None'}
                </td>
            </tr>
        );
    }, [renderCheck]);

    const browser = props.environment.browser;

    const tests = [
        renderTableCell(
            'Is this a supported browser',
            browser.isSupported ? 1 : 0,
            'Chrome, Firefox, Safari, Edge or Opera',
            browser.name,
            <span>Please use a supported browser. See <Link href='/browseroptions'>here</Link> for alternatives</span>
        ),
        renderTableCell(
            'Is this the most current version of the browser?',
            browser.isCurrent ? 1 : 0,
            currentVersions[browser.name],
            browser.version,
            'Update your browser.'
        ),
        renderTableCell(
            'Is the browser version at least recent?',
            browser.isMinimum ? 1 : 0,
            '>= ' + minimumVersions[browser.name],
            browser.version,
            (`Update your ${browser.name === 'Safari' ? 'operating system' : 'browser'}`)
        ),
        renderTableCell(
            'Is there available storage for offline usage?',
            browser.quotaTotal === -1 ? 2 : ((browser.quotaTotal - browser.quotaUsage) / 1_000_000) > 100 ? 2 : 0,
            '>= ' + 0,
            browser.quotaTotal === -1 ? 'Unknown' : `${util.numberWithCommas((browser.quotaTotal - browser.quotaUsage) / 1_000_000_000)}  out of  ${util.numberWithCommas(browser.quotaTotal / 1_000_000_000)} GB free`,
            !(browser.name.indexOf('Edge') >= 0 || browser.name.indexOf('Safari') >= 0)
            ? 'You may need to clear out some room on your device. Browsers generally allocate a fixed percentage of available storage for offline use.'
            : 'The Edge and Safari browsers do not report quota usage. You are probably fine.'
        )
    ];

    return (
        <ErrorBoundaryO uniqueKey='installation-check'>
            <div className="support-form__table">
                <table>
                    <thead>
                    <tr>
                        <th>Check</th>
                        <th>Status</th>
                        <th>Expected</th>
                        <th>Received</th>
                        <th>Resolution</th>
                    </tr>
                    </thead>
                    <tbody>{tests}</tbody>
                </table>
            </div>
        </ErrorBoundaryO>
    );

}

export type EnvironmentProps = {
    environment: EnvironmentResult;
}

export default Environment;